/**
 * AttachmentPickerSheet — popup dialog with Upload Photo / Take Photo / Upload File options.
 * Usage:
 *   <AttachmentPickerSheet
 *     open={open} onClose={() => setOpen(false)}
 *     onFiles={(files) => handleFiles(files)}  // receives File[]
 *   />
 */
import React, { useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Upload, Camera, FileText } from 'lucide-react';

export default function AttachmentPickerSheet({ open, onClose, onFiles }) {
  const photoRef = useRef();
  const fileRef  = useRef();
  const cameraRef = useRef();

  const handleFiles = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length) onFiles(files);
    e.target.value = '';
    onClose();
  };

  const handleCamera = async () => {
    onClose();
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      const video = document.createElement('video');
      video.srcObject = stream;
      video.play();
      await new Promise(resolve => { video.onloadedmetadata = resolve; });
      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      canvas.getContext('2d').drawImage(video, 0, 0);
      stream.getTracks().forEach(t => t.stop());
      canvas.toBlob(blob => {
        if (blob) onFiles([new File([blob], 'camera-photo.jpg', { type: 'image/jpeg' })]);
      }, 'image/jpeg', 0.9);
    } catch {
      // Fallback: open file input
      cameraRef.current?.click();
    }
  };

  const options = [
    {
      label: 'Upload Photo',
      icon: Upload,
      action: () => photoRef.current?.click(),
    },
    {
      label: 'Take Photo',
      icon: Camera,
      action: handleCamera,
    },
    {
      label: 'Upload File',
      icon: FileText,
      action: () => fileRef.current?.click(),
    },
  ];

  return (
    <>
      <input ref={photoRef} type="file" accept="image/*" multiple className="hidden" onChange={handleFiles} />
      <input ref={fileRef}  type="file" multiple className="hidden" onChange={handleFiles} />
      <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={handleFiles} />

      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="max-w-xs">
          <DialogHeader>
            <DialogTitle>Add Attachment</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 py-2">
            {options.map(({ label, icon: Icon, action }) => (
              <button
                key={label}
                type="button"
                onClick={action}
                className="w-full flex items-center gap-3 px-4 py-3 border rounded-lg hover:bg-muted/40 transition-colors text-sm font-medium"
              >
                <Icon className="w-5 h-5 text-muted-foreground" />
                {label}
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
